import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { CaretDown, Play, PlusCircle, Question, Spinner, Trash } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import {
  Button,
  Icon,
  IconButton,
  Input,
  MultiSelect,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@shopflo/ui'
import useSWR from 'swr'
import { getAvailableChannels, saveAppConfigV2, testAppConfigV2 } from 'src/lib/core/api-methods'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import {
  V2ConfigField,
  V2ConfigFieldOption,
  V2ConfigSection,
  V2IntegrationConfigSource,
  V2MerchantConfig
} from 'src/lib/types/appsIntegrations'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import DeleteConfirmationPopup from 'src/components/common/dialog/DeleteConfirmationPopup'

export interface ConfigSchemaFormHandle {
  validate: () => boolean
  save: () => Promise<boolean>
}

interface ConfigSchemaFormProps {
  appId: string
  configSchema: V2ConfigSection[]
  existingConfigs?: V2MerchantConfig[]
  integrationConfigSource?: V2IntegrationConfigSource
  multiConfig?: boolean
  onSaveSuccess?: (config: V2MerchantConfig) => void
  onDirtyChange?: (dirty: boolean) => void
}

interface ConfigInstance {
  key: string
  values: Record<string, unknown>
  enabled: boolean
  testing: boolean
  errors: Record<string, string>
}

const ARRAY_FIELD_TYPES = ['MULTI_SELECT', 'CHANNEL_SELECTOR', 'ARRAY']
const BOOLEAN_FIELD_TYPES = ['TOGGLE', 'SEGMENTED_TOGGLE']

const getFieldDefaultValue = (field: V2ConfigField): unknown => {
  if (ARRAY_FIELD_TYPES.includes(field.type)) return Array.isArray(field.defaultValue) ? field.defaultValue : []
  if (BOOLEAN_FIELD_TYPES.includes(field.type)) return field.defaultValue ?? false
  if (field.type === 'KEY_VALUE_MAP') {
    return field.defaultValue && typeof field.defaultValue === 'object'
      ? (field.defaultValue as Record<string, string>)
      : {}
  }
  return field.defaultValue ?? ''
}

const getAccountArrayField = (schema: V2ConfigSection[]): V2ConfigField | null => {
  const allFields = schema.flatMap(s => s.fields ?? [])
  return allFields.find(f => f.type === 'ARRAY' && (f.children?.length ?? 0) > 0) ?? null
}

const getAccountFieldDefs = (schema: V2ConfigSection[]): V2ConfigField[] => {
  const arrayField = getAccountArrayField(schema)
  return arrayField?.children ?? schema.flatMap(s => s.fields ?? [])
}

const ENABLED_FIELD_ID = 'enabled'

const emptyValues = (schema: V2ConfigSection[]): Record<string, unknown> =>
  Object.fromEntries(getAccountFieldDefs(schema).map(f => [f.id, getFieldDefaultValue(f)]))

const normalizeForCompare = (val: unknown): unknown => {
  if (val === null || val === undefined) return undefined
  if (typeof val === 'string') return val.length === 0 ? undefined : val
  if (Array.isArray(val)) {
    const cleaned = val.map(normalizeForCompare).filter(v => v !== undefined)
    return cleaned.length === 0 ? undefined : cleaned
  }
  if (typeof val === 'object') {
    const entries = Object.entries(val as Record<string, unknown>)
      .map(([k, v]) => [k, normalizeForCompare(v)] as const)
      .filter(([, v]) => v !== undefined)
    return entries.length === 0 ? undefined : Object.fromEntries(entries)
  }
  return val
}

const normalizedValuesSignature = (values: Record<string, unknown>): string => {
  const normalized = normalizeForCompare(values) ?? {}
  // Sort keys so attribute insertion order doesn't flip the signature.
  const stableStringify = (v: unknown): string => {
    if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`
    if (v && typeof v === 'object') {
      const keys = Object.keys(v as Record<string, unknown>).sort()
      return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify((v as Record<string, unknown>)[k])}`).join(',')}}`
    }
    return JSON.stringify(v)
  }
  return stableStringify(normalized)
}

type SaveConfigApiResponse = {
  data?: {
    success?: boolean
    error?: { message?: string }
    data?: V2MerchantConfig | string[]
  }
}

const unwrapErrorMessage = (message: string): string => {
  const braceIndex = message.indexOf('{')
  if (braceIndex !== -1) {
    try {
      const parsed = JSON.parse(message.slice(braceIndex))
      const inner = parsed?.error?.message ?? parsed?.message
      if (typeof inner === 'string' && inner.trim()) return unwrapErrorMessage(inner)
    } catch {
      const cleaned = message.replace(/\s*\(downstream[^)]*\):.*/i, '').trim()
      if (cleaned) return cleaned
    }
  }
  return message.trim()
}

type TestResultData = { success?: boolean; message?: string; error_snippet?: string }
type TestResponseBody = TestResultData & { data?: TestResultData }

const getTestFailureMessage = (result?: TestResultData): string | undefined => {
  if (result?.error_snippet) {
    try {
      const parsed = JSON.parse(result.error_snippet)
      const msg = parsed?.errorMessage ?? parsed?.error?.message ?? parsed?.message
      if (typeof msg === 'string' && msg.trim()) return msg
    } catch {
    }
  }
  return result?.message
}

const getSaveConfigErrorMessage = (response: SaveConfigApiResponse): string => {
  const body = response?.data
  if (body?.error?.message) return unwrapErrorMessage(body.error.message)
  const payload = body?.data
  if (Array.isArray(payload) && typeof payload[0] === 'string') return payload[0]
  return 'Failed to save configuration.'
}

const sanitizeConfigValues = (values: Record<string, unknown>): Record<string, unknown> => {
  const clean = (val: unknown): unknown => {
    if (Array.isArray(val)) {
      return val.filter(item => item !== null && item !== undefined).map(clean)
    }
    if (val && typeof val === 'object') {
      return Object.fromEntries(
        Object.entries(val as Record<string, unknown>)
          .filter(([, v]) => v !== null && v !== undefined)
          .map(([k, v]) => [k, clean(v)])
      )
    }
    if (typeof val === 'string') return val.trim()
    return val
  }
  return clean(values) as Record<string, unknown>
}

const ACCOUNTS_KEY = 'accounts'

const buildConfigValues = (
  schema: V2ConfigSection[],
  instances: ConfigInstance[]
): Record<string, unknown> => {
  const accountFieldIds = getAccountFieldDefs(schema)
    .map(field => field.id)
    .filter(id => id !== ENABLED_FIELD_ID)
  const pickFields = (inst: ConfigInstance): Record<string, unknown> => {
    const out: Record<string, unknown> = {}
    accountFieldIds.forEach(id => {
      if (inst.values[id] !== undefined) out[id] = inst.values[id]
    })
    return out
  }

  const arrayField = getAccountArrayField(schema)
  if (arrayField) {
    return {
      [arrayField.id]: instances.map(inst => ({ ...pickFields(inst), [ENABLED_FIELD_ID]: inst.enabled }))
    }
  }
  return pickFields(instances[0] ?? { key: '', values: {}, enabled: false, testing: false, errors: {} })
}

const buildInstances = (schema: V2ConfigSection[], configs: V2MerchantConfig[]): ConfigInstance[] => {
  if (configs.length === 0) {
    return [{ key: 'inst-0', values: emptyValues(schema), enabled: true, testing: false, errors: {} }]
  }

  const arrayKey = getAccountArrayField(schema)?.id ?? ACCOUNTS_KEY
  const findArrayAccounts = (cv: Record<string, unknown>): Record<string, unknown>[] | null => {
    if (Array.isArray(cv[arrayKey])) return cv[arrayKey] as Record<string, unknown>[]
    const fieldDefs = getAccountFieldDefs(schema)
    if (!fieldDefs.length) return null
    for (const [, v] of Object.entries(cv)) {
      if (!Array.isArray(v) || !v.length) continue
      const first = v[0]
      if (!first || typeof first !== 'object' || Array.isArray(first)) continue
      const keys = Object.keys(first as Record<string, unknown>)
      const matches = fieldDefs.filter(f => keys.includes(f.id)).length
      if (matches >= 2) return v as Record<string, unknown>[]
    }
    return null
  }
  const allInstances: ConfigInstance[] = []
  configs.forEach(c => {
    const cv = (c.configValues ?? {}) as Record<string, unknown>
    const accounts = findArrayAccounts(cv)

    if (accounts) {
      accounts.forEach(acc => {
        const account = (acc ?? {}) as Record<string, unknown>
        allInstances.push({
          key: `inst-${allInstances.length}`,
          values: { ...emptyValues(schema), ...account },
          enabled: account.enabled === true,
          testing: false,
          errors: {}
        })
      })
    } else {
      const fieldDefs = getAccountFieldDefs(schema)
      const matchScore = (obj: Record<string, unknown>): number =>
        fieldDefs.filter(f => Object.prototype.hasOwnProperty.call(obj, f.id)).length
      let bestValues: Record<string, unknown> = cv
      let bestScore = matchScore(cv)
      const visit = (node: unknown) => {
        if (!node || typeof node !== 'object' || Array.isArray(node)) return
        const obj = node as Record<string, unknown>
        const score = matchScore(obj)
        if (score >= 2 && score > bestScore) {
          bestScore = score
          bestValues = obj
        }
        Object.values(obj).forEach(visit)
      }
      visit(cv)
      const providerMeta =
        cv.provider_metadata && typeof cv.provider_metadata === 'object' && !Array.isArray(cv.provider_metadata)
          ? (cv.provider_metadata as Record<string, unknown>)
          : {}
      allInstances.push({
        key: `inst-${allInstances.length}`,
        values: { ...emptyValues(schema), ...bestValues, ...providerMeta },
        enabled: c.active ?? false,
        testing: false,
        errors: {}
      })
    }
  })

  const result = allInstances.length
    ? allInstances
    : [{ key: 'inst-0', values: emptyValues(schema), enabled: true, testing: false, errors: {} }]

  if (result.length === 1) result[0] = { ...result[0], enabled: true }
  return result
}

const ERROR_HOVER_CLASS =
  '[&:hover:not(:focus)]:!shadow-[0px_0px_0px_1.5px_#ff6467,0px_1.5px_4px_0px_rgba(26,26,26,0.12)]'

const KeyValueMapField: React.FC<{
  value: unknown
  onChange: (next: Record<string, string>) => void
}> = ({ value, onChange }) => {
  const incoming: Record<string, string> = useMemo(
    () =>
      value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, string>) : {},
    [value]
  )

  const [rows, setRows] = useState<{ key: string; value: string }[]>(() => {
    const initial = Object.entries(incoming).map(([k, v]) => ({ key: k, value: String(v ?? '') }))
    return initial.length ? initial : [{ key: '', value: '' }]
  })

  const rowsToObject = (rs: { key: string; value: string }[]) => {
    const out: Record<string, string> = {}
    rs.forEach(r => {
      const k = r.key.trim()
      if (k) out[k] = r.value
    })
    return out
  }
  useEffect(() => {
    const current = rowsToObject(rows)
    const same =
      Object.keys(current).length === Object.keys(incoming).length &&
      Object.keys(current).every(k => incoming[k] === current[k])
    if (!same) {
      const next = Object.entries(incoming).map(([k, v]) => ({ key: k, value: String(v ?? '') }))
      setRows(next.length ? next : [{ key: '', value: '' }])
    }
  }, [incoming])

  const update = (next: { key: string; value: string }[]) => {
    setRows(next.length ? next : [{ key: '', value: '' }])
    onChange(rowsToObject(next))
  }

  return (
    <div className='flex flex-col gap-1 mb-3'>
      <div className='flex flex-col gap-2 py-2'>
        {rows.map((row, idx) => (
        <div key={idx} className='flex items-end gap-2'>
          <div className='flex-1 flex flex-col gap-1'>
            <span className='text-text-xs-medium text-zinc-500 h-5'>Header Name</span>
            <Input
              variant='outline'
              dimension='md'
              placeholder='Placeholder'
              value={row.key}
              onChange={e => {
                const next = rows.slice()
                next[idx] = { ...next[idx], key: (e.target as HTMLInputElement).value }
                update(next)
              }}
              className='placeholder:text-[#9F9FA9]'
            />
          </div>
          <div className='flex-1 flex flex-col gap-1'>
            <span className='text-text-xs-medium text-zinc-500 h-5'>Header Value</span>
            <Input
              variant='outline'
              dimension='md'
              placeholder='Placeholder'
              value={row.value}
              onChange={e => {
                const next = rows.slice()
                next[idx] = { ...next[idx], value: (e.target as HTMLInputElement).value }
                update(next)
              }}
              className='placeholder:text-[#9F9FA9]'
            />
          </div>
          <div className='flex flex-col gap-1'>
            <span className='h-5' aria-hidden />
            <div className='flex h-9 items-center'>
              <IconButton
                variant='ghost'
                size='xs'
                IconComponent={Trash}
                aria-label='Remove row'
                onClick={() => update(rows.filter((_, i) => i !== idx))}
                className='text-zinc-500 hover:text-red-600'
              />
            </div>
          </div>
        </div>
      ))}
      </div>
      
      <Button
        variant='dashed'
        size='sm'
        leftIcon={PlusCircle}
        iconProps={{ leftIconProps: { className: 'text-zinc-400' } }}
        className='bg-transparent h-7 w-fit text-text-sm-medium'
        onClick={() => update([...rows, { key: '', value: '' }])}
      >
        Add Header
      </Button>
    </div>
  )
}

const FieldRow: React.FC<{
  field: V2ConfigField
  value: unknown
  error?: string
  onChange: (id: string, value: unknown) => void
  channelOptions?: V2ConfigFieldOption[]
}> = ({ field, value, error, onChange, channelOptions }) => {
  if (field.hidden) return null

  const strVal = value !== undefined && value !== null ? String(value) : ''

  const handleTextChange = (val: string) => {
    onChange(field.id, val.replace(/^\s+/, ''))
  }

  const renderInput = () => {
    if (field.type === 'TOGGLE') {
      return (
        <FloCheckbox
          type='SWITCH'
          value={Boolean(value ?? field.defaultValue)}
          setValue={checked => onChange(field.id, checked)}
        />
      )
    }

    if (field.type === 'SEGMENTED_TOGGLE') {
      // Width matches the Header Name input above: half the field column, less the 20px delete
      // button gutter and the two 8px gaps of that row.
      return (
        <div className='w-[calc(50%-18px)]'>
          <SegmentedControl
            variant='bright'
            className='h-9 w-full'
            value={Boolean(value ?? field.defaultValue)}
            onChange={val => onChange(field.id, val)}
            items={[
              { value: true, label: 'Enable' },
              { value: false, label: 'Disable' }
            ]}
          />
        </div>
      )
    }

    if (field.type === 'SELECT' && field.options?.length) {
      return (
        <Select value={strVal || undefined} onValueChange={val => onChange(field.id, val)}>
          <div
            className={error ? 'rounded-md shadow-[0px_0px_0px_1.5px_#ff6467,0px_1.5px_4px_0px_rgba(26,26,26,0.12)]' : undefined}
          >
          <SelectTrigger className='w-full !bg-white data-[placeholder]:!text-[#9F9FA9]'>
            <SelectValue placeholder={field.placeholder ?? 'Choose'} />
          </SelectTrigger>
          </div>
          <SelectContent>
            {field.options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    if (field.type === 'MULTI_SELECT' || field.type === 'CHANNEL_SELECTOR') {
      const opts: V2ConfigFieldOption[] | undefined =
        field.type === 'CHANNEL_SELECTOR' ? channelOptions : field.options
      if (!opts?.length) return null
      const selectedArr: string[] = Array.isArray(value) ? (value as string[]) : []

      return (
        <div className='w-full [&_button]:!bg-white [&_.ui-text-muted-foreground]:!text-[#9F9FA9]'>
          <MultiSelect
            options={opts.map(opt => ({ value: opt.value, label: opt.label }))}
            value={selectedArr}
            onValueChange={selected => onChange(field.id, selected)}
            placeholder={field.placeholder ?? 'Choose'}
            size='md'
            searchable
            className='w-full'
            endIcon={<Icon IconComponent={CaretDown} size='xxs' color='text-zinc-500' />}
          />
        </div>
      )
    }

    if (field.type === 'TEXTAREA') {
      return (
        <Textarea
          variant='outline'
          dimension='md'
          error={Boolean(error)}
          placeholder={field.placeholder ?? ''}
          value={strVal}
          onChange={e => handleTextChange(e.target.value)}
          className={`placeholder:text-[#9F9FA9]${error ? ` ${ERROR_HOVER_CLASS}` : ''}`}
        />
      )
    }

    if (field.type === 'KEY_VALUE_MAP') {
      return <KeyValueMapField value={value} onChange={next => onChange(field.id, next)} />
    }

    const inputType =
      field.type === 'PASSWORD' || field.type === 'SECRET'
        ? 'password'
        : field.type === 'NUMBER'
          ? 'number'
          : field.type === 'EMAIL'
            ? 'email'
            : field.type === 'URL'
              ? 'url'
              : 'text'

    return (
      <Input
        type={inputType}
        placeholder={field.placeholder ?? ''}
        value={strVal}
        onChange={e => handleTextChange((e.target as HTMLInputElement).value)}
        variant='outline'
        dimension='md'
        error={Boolean(error)}
        className={`placeholder:text-[#9F9FA9]${error ? ` ${ERROR_HOVER_CLASS}` : ''}`}
      />
    )
  }

  if (field.type === 'TOGGLE') {
    return (
      <div className='flex items-center justify-between py-1'>
        <span className='text-text-xs-medium text-zinc-600'>
          {field.label}
        </span>
        {renderInput()}
        {error && <p className='text-text-xs-regular text-red-600 mt-1'>{error}</p>}
      </div>
    )
  }

  return (
    <div className={`grid grid-cols-[180px_1fr] items-start gap-4${field.type === 'SEGMENTED_TOGGLE' ? ' pt-4' : ''}`}>
      {field.label && (
        <span className={`flex h-9 items-center gap-1.5 text-text-sm-regular text-zinc-700${field.type === 'KEY_VALUE_MAP' ? ' mt-8' : ''}`}>
          {field.label}
          {field.labelTooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='flex shrink-0 cursor-pointer'>
                  <Icon
                    IconComponent={Question}
                    size='xxs'
                    className='text-zinc-400'
                    iconProps={{ weight: 'regular' }}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent className='max-w-[280px] !text-wrap !text-left'>{field.labelTooltip}</TooltipContent>
            </Tooltip>
          )}
        </span>
      )}
      <div>
        {renderInput()}
        {error && <p className='text-text-xs-regular text-red-600 mt-1'>{error}</p>}
      </div>
    </div>
  )
}

const ConfigSchemaForm = forwardRef<ConfigSchemaFormHandle, ConfigSchemaFormProps>(
  (
    { appId, configSchema, existingConfigs = [], integrationConfigSource, multiConfig, onSaveSuccess, onDirtyChange },
    ref
  ) => {
    const { t } = useLocale()
    const [instances, setInstances] = useState<ConfigInstance[]>(() =>
      buildInstances(configSchema, existingConfigs)
    )
    const [baseline, setBaseline] = useState<ConfigInstance[]>(() =>
      buildInstances(configSchema, existingConfigs)
    )
    const instanceCounterRef = useRef(0)
    const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null)
    const previousConfigSignatureRef = useRef<string>('')
    const justSavedRef = useRef(false)

    const hasChannelField = useMemo(
      () => getAccountFieldDefs(configSchema).some(f => f.type === 'CHANNEL_SELECTOR'),
      [configSchema]
    )
    const { data: channelsData } = useSWR(
      hasChannelField ? '/merchant-app/listing' : null,
      () => getAvailableChannels()
    )
    const channelOptions: V2ConfigFieldOption[] = useMemo(() => {
      const list = (channelsData as { data?: { id: string; name: string }[] } | undefined)?.data
      return Array.isArray(list) ? list.map(c => ({ value: c.id, label: c.name })) : []
    }, [channelsData])

    useEffect(() => {
      const currentSignature = normalizedValuesSignature(
        Object.fromEntries((existingConfigs ?? []).map(c => [c.configSectionId, c.configValues]))
      )
      if (configSchema !== null && previousConfigSignatureRef.current !== currentSignature) {
        previousConfigSignatureRef.current = currentSignature
        if (justSavedRef.current) {
          justSavedRef.current = false
          return
        }
        const fresh = buildInstances(configSchema, existingConfigs)
        setInstances(fresh)
        setBaseline(fresh)
      }
    }, [configSchema, existingConfigs])

    useEffect(() => {
      if (instances.length === 1 && !instances[0].enabled) {
        setInstances(prev => (prev.length === 1 ? [{ ...prev[0], enabled: true }] : prev))
      }
    }, [instances])

    const isDirty = useMemo(() => {
      if (instances.length !== baseline.length) return true
      return instances.some((inst, i) => {
        const base = baseline[i]
        if (!base) return true
        if (inst.enabled !== base.enabled) return true
        return normalizedValuesSignature(inst.values) !== normalizedValuesSignature(base.values)
      })
    }, [instances, baseline])

    useEffect(() => {
      onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    const updateInstance = useCallback((key: string, patch: Partial<ConfigInstance>) => {
      setInstances(prev => prev.map(inst => (inst.key === key ? { ...inst, ...patch } : inst)))
    }, [])

    const handleFieldChange = useCallback((instanceKey: string, fieldId: string, val: unknown) => {
      setInstances(prev =>
        prev.map(inst => {
          if (inst.key !== instanceKey) return inst
          const nextErrors = { ...inst.errors }
          delete nextErrors[fieldId]
          return { ...inst, values: { ...inst.values, [fieldId]: val }, errors: nextErrors }
        })
      )
    }, [])

    const validateInstance = useCallback(
      (inst: ConfigInstance): Record<string, string> => {
        const errs: Record<string, string> = {}
        for (const field of getAccountFieldDefs(configSchema)) {
          if (field.hidden) continue
          if (field.required) {
            const v = inst.values[field.id]
            const isEmpty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)
            if (isEmpty) {
              const fieldName = field.label ?? field.id.charAt(0).toUpperCase() + field.id.slice(1)
              errs[field.id] = `${fieldName} is required`
            }
          }
        }
        return errs
      },
      [configSchema]
    )

    const saveAll = useCallback(async (): Promise<boolean> => {
      let allValid = true
      const validated = instances.map(inst => {
        // Disabled configurations are not submitted, so their fields aren't required.
        if (!inst.enabled) return { ...inst, errors: {} }
        const errs = validateInstance(inst)
        if (Object.keys(errs).length > 0) {
          allValid = false
          return { ...inst, errors: errs }
        }
        return inst
      })

      if (!validated.some(inst => inst.enabled)) {
        setInstances(validated)
        errorToast('Enable at least one configuration before saving.')
        return false
      }

      if (!allValid) {
        setInstances(validated)
        return false
      }
      const toSubmit = validated.filter(inst => inst.enabled || normalizeForCompare(inst.values) !== undefined)
      const configValues = buildConfigValues(configSchema, toSubmit)

      try {
        const response = (await saveAppConfigV2(appId, {
          config_values: sanitizeConfigValues(configValues)
        } as unknown as Parameters<typeof saveAppConfigV2>[1])) as SaveConfigApiResponse

        if (!response?.data?.success) {
          const errorMsg = getSaveConfigErrorMessage(response)
          errorToast(errorMsg)
          return false
        }

        justSavedRef.current = true
        setBaseline(instances.map(i => ({ ...i })))
        const savedConfig = response.data.data
        if (onSaveSuccess && savedConfig && !Array.isArray(savedConfig)) onSaveSuccess(savedConfig)
        successToast('Configuration saved.')
        return true
      } catch (e: unknown) {
        const rawMsg =
          (e as { response?: { data?: { error?: { message?: string }; message?: string } } })?.response?.data?.error
            ?.message ??
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        errorToast(rawMsg ? unwrapErrorMessage(rawMsg) : 'Failed to save configuration.')
        return false
      }
    }, [appId, configSchema, instances, validateInstance, onSaveSuccess, multiConfig])

    const validateAll = useCallback((): boolean => {
      let allValid = true
      const validated = instances.map(inst => {
        if (!inst.enabled) return { ...inst, errors: {} }
        const errs = validateInstance(inst)
        if (Object.keys(errs).length > 0) {
          allValid = false
          return { ...inst, errors: errs }
        }
        return inst
      })
      if (!allValid) setInstances(validated)
      return allValid
    }, [instances, validateInstance])

    useImperativeHandle(ref, () => ({ validate: validateAll, save: saveAll }), [validateAll, saveAll])

    const handleTest = useCallback(
      async (instKey: string) => {
        const currentInst = instances.find(i => i.key === instKey)
        if (!currentInst) return

        const errs = validateInstance(currentInst)
        if (Object.keys(errs).length > 0) {
          updateInstance(instKey, { errors: errs })
          return
        }

        updateInstance(instKey, { testing: true })
        try {
          const testValues = buildConfigValues(configSchema, [currentInst])
          const arrayField = getAccountArrayField(configSchema)
          if (arrayField) {
            const arr = (testValues[arrayField.id] as Record<string, unknown>[]) ?? []
            testValues[arrayField.id] = arr.map(({ [ENABLED_FIELD_ID]: _e, ...rest }) => rest)
          }
          const res = (await testAppConfigV2(
            appId,
            sanitizeConfigValues({ [ENABLED_FIELD_ID]: true, ...testValues })
          )) as unknown
          const outer = (res as { data?: TestResponseBody })?.data
          const result = outer?.data ?? outer
          if (result?.success) {
            successToast(result.message ?? t('apps_integrations_test_passed'))
          } else {
            errorToast(getTestFailureMessage(result) ?? t('apps_integrations_test_failed'))
          }
        } catch (e: unknown) {
          const msg =
            (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? t('apps_integrations_test_failed')
          errorToast(msg)
        } finally {
          updateInstance(instKey, { testing: false })
        }
      },
      [appId, configSchema, instances, updateInstance, validateInstance, t]
    )

    const addInstance = useCallback(() => {
      instanceCounterRef.current += 1
      setInstances(prev => [
        ...prev,
        {
          key: `inst-new-${instanceCounterRef.current}`,
          values: emptyValues(configSchema),
          enabled: true,
          testing: false,
          errors: {}
        }
      ])
    }, [configSchema])

    const deleteInstance = useCallback((instKey: string) => {
      setInstances(prev => prev.filter(i => i.key !== instKey))
    }, [])

    const requestDelete = useCallback(
      (inst: ConfigInstance) => {
        if (normalizeForCompare(inst.values) === undefined) deleteInstance(inst.key)
        else setPendingDeleteKey(inst.key)
      },
      [deleteInstance]
    )

    const supportsTest = integrationConfigSource === 'ANALYTICS_BACKEND'
    const allFields = getAccountFieldDefs(configSchema).filter(f => !f.hidden && f.id !== ENABLED_FIELD_ID)

    if (!allFields.length) {
      return (
        <p className='text-text-sm-regular text-zinc-500'>No configuration required for this integration.</p>
      )
    }

    const baseTitle = configSchema[0]?.title ?? 'Configuration'

    return (
      <div>
        {instances.map((inst, idx) => {
          const visibleFields = getAccountFieldDefs(configSchema).filter(f => !f.hidden && f.id !== ENABLED_FIELD_ID)
          const title = instances.length > 1 ? `${baseTitle.replace(/\s*\d+$/, '')} ${idx + 1}` : baseTitle
          const showDivider = idx < instances.length - 1 || multiConfig

          return (
            <div
              key={inst.key}
              className={`flex flex-col pb-6 pt-4 gap-1${showDivider ? ' border-b border-zinc-200' : ''}`}
            >
              <div className='flex items-center gap-3 py-2'>
                {instances.length > 1 && (
                  <FloCheckbox
                    type='SWITCH'
                    value={inst.enabled}
                    setValue={v => updateInstance(inst.key, { enabled: v })}
                  />
                )}
                <span className='text-text-sm-medium text-zinc-500'>{title}</span>
                <div className='flex-1 h-px bg-zinc-200' />
                {supportsTest && (
                  <button
                    type='button'
                    onClick={() => handleTest(inst.key)}
                    disabled={inst.testing}
                    className='flex items-center gap-0.5 text-text-xs-semibold text-zinc-600 hover:text-zinc-700 transition-colors disabled:opacity-50'
                  >
                    <Icon
                      IconComponent={inst.testing ? Spinner : Play}
                      size='sm'
                      className={inst.testing ? 'text-zinc-500 animate-spin' : 'text-zinc-500'}
                    />
                    {inst.testing ? 'Testing…' : 'Test'}
                  </button>
                )}
                {supportsTest && idx > 0 && <div className='w-px h-4 bg-zinc-200' />}
                {idx > 0 && (
                  <IconButton
                    variant='ghost'
                    size='sm'
                    IconComponent={Trash}
                    aria-label={`Delete ${title}`}
                    onClick={() => requestDelete(inst)}
                    className='text-zinc-600 hover:text-red-600 hover:bg-red-50'
                  />
                )}
              </div>

              <div className='space-y-2 py-1.5'>
                {visibleFields.map(field => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    value={inst.values[field.id]}
                    error={inst.errors[field.id]}
                    onChange={(id, val) => handleFieldChange(inst.key, id, val)}
                    channelOptions={channelOptions}
                  />
                ))}
              </div>
            </div>
          )
        })}
        {multiConfig && (
          <div className='my-3'>
            <Button
              variant='dashed'
              size='sm'
              leftIcon={PlusCircle}
              iconProps={{ leftIconProps: { className: 'text-zinc-400' } }}
              className='bg-transparent h-7 w-fit text-text-sm-medium'
              onClick={addInstance}
            >
              Add Configuration
            </Button>
          </div>
        )}

        <DeleteConfirmationPopup
          isOpen={!!pendingDeleteKey}
          actions={{
            onConfirm: () => {
              if (pendingDeleteKey) deleteInstance(pendingDeleteKey)
              setPendingDeleteKey(null)
            },
            onClose: () => setPendingDeleteKey(null)
          }}
          config={{
            title: 'Remove this configuration?',
            description: 'The credentials entered in this set will be removed.',
            confirmationText: 'Yes, Remove'
          }}
        />
      </div>
    )
  }
)
ConfigSchemaForm.displayName = 'ConfigSchemaForm'

export default ConfigSchemaForm
